/**
 * Native Cognito email OTP sign-in.
 *
 * Uses Cognito's built-in passwordless EMAIL_OTP factor via the `USER_AUTH`
 * flow — NOT a custom-auth challenge. The code is generated, stored, and
 * validated by Cognito and is bound to the originating `InitiateAuth` session,
 * so it is intentionally NOT redeemable on another device (no portable link).
 * That session-stickiness is the protection against cross-device session
 * phishing; the email is code-only.
 *
 * The magic link flow (`cognitoAuth.ts`) is left untouched as the revert path.
 *
 * Two call sites, both on the SAME browser that requested the code (the Amplify
 * challenge session lives in memory there):
 * - `requestEmailOtp` — start the flow; Cognito emails the code.
 * - `verifyEmailOtp` — submit the typed code and finish sign-in.
 */
import { confirmSignIn, signIn, signOut } from "aws-amplify/auth";

import { type CognitoUserCredential, ensureCognitoUser } from "./cognitoAuth";
import { fetchAuthSessionWithLock } from "./cognitoSessionLock";

// Amplify's confirm step for the native EMAIL_OTP factor (USER_AUTH flow).
const EMAIL_OTP_CODE_STEP = "CONFIRM_SIGN_IN_WITH_EMAIL_CODE";

// Amplify's step when Cognito won't auto-select EMAIL_OTP and instead returns
// the list of available first factors (SELECT_CHALLENGE). This happens when
// EMAIL_OTP is not an eligible first factor for the user — e.g. the account can
// only use a password, or is in FORCE_CHANGE_PASSWORD and must complete a
// password sign-in first. The caller routes these users to password sign-in.
const FIRST_FACTOR_SELECTION_STEP = "CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION";

function isUserAlreadyAuthenticatedException(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === "object" &&
    "name" in error &&
    (error as { name: string }).name === "UserAlreadyAuthenticatedException"
  );
}

/**
 * Amplify v6 converts Cognito's PasswordResetRequiredException into a
 * RESET_PASSWORD next step instead of throwing. Surface it as an error so
 * callers can show the right message.
 */
function throwIfPasswordResetRequired(result: { nextStep: { signInStep: string } }): void {
  if (result.nextStep.signInStep === "RESET_PASSWORD") {
    const error = new Error("Password reset required for the user");
    error.name = "PasswordResetRequiredException";
    throw error;
  }
}

function buildEmailOtpSignInInput(username: string) {
  return {
    username,
    options: {
      authFlowType: "USER_AUTH" as const,
      preferredChallenge: "EMAIL_OTP" as const,
    },
  };
}

/**
 * Starts the native email OTP flow: JIT-imports legacy workplace users, then
 * initiates the `USER_AUTH` flow selecting the EMAIL_OTP factor. Cognito sends
 * the code (delivered via Knock by the CustomEmailSender Lambda) and leaves the
 * Amplify session awaiting the code, so the same browser can `verifyEmailOtp`.
 *
 * Returns the Cognito username auth was initiated with. Pass it back as
 * `username` on a resend so the resend reuses the resolved username instead of
 * re-running the JIT import — important for unverified email-alias users, whose
 * email alias can't initiate auth on its own. `hasVerifiedEmail` is present
 * when this call ran the PreSignUp probe.
 */
export async function requestEmailOtp(params: {
  email: string;
  skipEnsureUser?: boolean;
  username?: string;
}): Promise<{
  username: string;
  hasUsablePassword?: boolean;
  hasVerifiedEmail?: boolean;
}> {
  const { email, skipEnsureUser = false, username: providedUsername } = params;

  let username: string;
  let hasUsablePassword: boolean | undefined;
  let hasVerifiedEmail: boolean | undefined;
  if (providedUsername) {
    username = providedUsername;
  } else if (skipEnsureUser) {
    username = email;
  } else {
    const ensured = await ensureCognitoUser(email);
    username = ensured.cognitoUsername;
    hasUsablePassword = ensured.hasUsablePassword;
    hasVerifiedEmail = ensured.hasVerifiedEmail;
  }

  let result;
  try {
    result = await signIn(buildEmailOtpSignInInput(username));
  } catch (error: unknown) {
    if (!isUserAlreadyAuthenticatedException(error)) {
      throw error;
    }
    // A previous Cognito session still exists. Clear it and retry.
    await signOut();
    result = await signIn(buildEmailOtpSignInInput(username));
  }

  throwIfPasswordResetRequired(result);

  if (result.nextStep.signInStep === FIRST_FACTOR_SELECTION_STEP) {
    // EMAIL_OTP isn't available for this user; Cognito only offers password-based
    // factors. Clear the in-progress USER_AUTH flow so a subsequent password
    // sign-in starts clean, then signal the caller to route to password sign-in.
    await signOut();
    const error = new Error("Email code sign-in is not available for this account");
    error.name = "EmailOtpNotAvailableException";
    throw error;
  }

  if (result.nextStep.signInStep !== EMAIL_OTP_CODE_STEP) {
    throw new Error(`Expected ${EMAIL_OTP_CODE_STEP} but got ${result.nextStep.signInStep}`);
  }

  if (typeof hasUsablePassword === "boolean") {
    return { username, hasUsablePassword, hasVerifiedEmail };
  }
  return { username };
}

/**
 * Submits the code typed on the requesting browser, completing the native
 * EMAIL_OTP challenge against the in-memory Amplify session.
 */
export async function verifyEmailOtp(code: string): Promise<CognitoUserCredential> {
  const result = await confirmSignIn({ challengeResponse: code });

  throwIfPasswordResetRequired(result);

  if (result.isSignedIn) {
    const session = await fetchAuthSessionWithLock();
    const idToken = session.tokens?.idToken?.toString();

    if (!idToken) {
      throw new Error("Missing Cognito id token after authentication");
    }

    return {
      idToken,
      accessToken: session.tokens?.accessToken?.toString(),
    };
  }

  throw new Error("Authentication not completed");
}
